import React from 'react';
import { useTranslation } from '../../../translations';
import PrintFrame from '../../../components/print/PrintFrame';
import { formatDate, formatDateTime, localeTag } from '../../../utils/format';
import { formatYMD, planGroupHasSizes } from '../utils';
import { PrintDoc } from './dayPlan.styles';

// U.29: plan dnia na papier. Ten sam `plan` co na ekranie (calendar/utils.js#buildDayPlan), ale
// rozpisany do pracy przy ladzie: przy każdym wydaniu i zwrocie lista sztuk (typ, nazwa, rozmiar,
// numer) i kratka do odhaczenia. Drukuje się z ukrytej ramki, nie ze strony panelu
// (components/print/PrintFrame.js mówi, dlaczego). Trwające są skrócone do jednej linii: tego
// dnia nic się z nimi nie robi. Na kartce jest imię i nazwisko klienta, BEZ telefonu: plan leży
// przy ladzie, a numer jest w szczegółach rezerwacji.
//
// `React.memo`: dokument siedzi w ukrytej ramce cały czas, a ekran kalendarza renderuje się przy
// każdej zmianie stanu; przerysowujemy go tylko, gdy zmieni się plan, dzień albo magazyn.
function DayPlanPrint({ frameRef, plan, date, equipmentState, printedAt }) {
  const { t, currentLanguage } = useTranslation();
  const title = t('calendar.dayPlan.printTitle');
  const day = (value) => formatDate(value, { locale: currentLanguage });
  const dayKey = formatYMD(date);
  const { prepare } = plan;
  // Bez mapy magazynu sztuki nie mają nazw: zamiast listy numerów sztuk drukujemy samą liczbę.
  const itemsNamed = equipmentState === 'ready';

  // Sekcje są ułożone po NAZWISKU, więc to ono jest pogrubione: przy ladzie klient podaje
  // nazwisko i po nim szuka się go na kartce.
  const clientName = (r) => {
    const first = String(r.firstname || '').trim();
    const last = String(r.lastname || '').trim();
    if (!first && !last) return <b>{t('calendar.dayPlan.reservationNo', { id: r.id })}</b>;
    return last ? <>{first ? `${first} ` : ''}<b>{last}</b></> : <b>{first}</b>;
  };

  // X.1: przy sztuce już wydanej (sekcja wydań) albo już zwróconej (zwroty) dopisek, żeby kartka
  // wydrukowana w trakcie dnia nie kazała wydawać drugi raz tego, co wyszło rano.
  const itemLine = (item, kind) => {
    const done = kind === 'pickup' && item.pickedUp ? t('calendar.dayPlan.itemPickedUp')
      : kind === 'return' && item.returned ? t('calendar.dayPlan.itemReturned') : null;
    // N.1: sztuka do wydania, która tego dnia ma jeszcze wrócić od poprzedniego klienta.
    const backToday = kind === 'pickup' && item.returnsToday ? t('calendar.dayPlan.itemReturnsToday') : null;
    if (!item.known) return [t('calendar.dayPlan.unknownItem', { id: item.id }), done, backToday].filter(Boolean).join(' · ');
    const name = [item.type, item.name].filter(Boolean).join(': ');
    return [
      name || t('calendar.dayPlan.unknownItem', { id: item.id }),
      item.size,
      item.inventoryNumber && t('calendar.dayPlan.inventoryNo', { number: item.inventoryNumber }),
      done,
      backToday,
    ].filter(Boolean).join(' · ');
  };

  // `kind`: wydanie pokazuje, do kiedy sprzęt wychodzi; zwrot, od kiedy był u klienta.
  const entry = ({ reservation: r, items, oneDay }, kind) => {
    const when = kind === 'pickup'
      ? (oneDay ? t('calendar.dayPlan.sameDayReturn') : t('calendar.dayPlan.until', { date: day(r.end_date) }))
      : (oneDay ? t('calendar.dayPlan.sameDayPickup') : t('calendar.dayPlan.since', { date: day(r.start_date) }));
    return (
      <div className="entry" key={r.id} data-cy={`day-plan-print-${kind}`}>
        <span className="tick" />
        <div className="entry-main">
          {clientName(r)}
          <span className="meta">{` · ${when}`}</span>
          {items.length === 0 && <div className="muted">{t('calendar.dayPlan.noEquipment')}</div>}
          {items.length > 0 && itemsNamed && (
            <ul>{items.map((item) => <li key={item.id}>{itemLine(item, kind)}</li>)}</ul>
          )}
          {items.length > 0 && !itemsNamed && (
            <div className="muted">
              {[
                t('calendar.dayPlan.pieces', { count: items.length }),
                kind === 'pickup' && items.some((item) => item.returnsToday)
                  && t('calendar.dayPlan.prepareReturningToday', { count: items.filter((item) => item.returnsToday).length }),
              ].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const heading = (label, count) => <h2>{label} ({count})</h2>;

  return (
    <PrintFrame
      frameRef={frameRef}
      title={title}
      documentTitle={`${title} ${dayKey}`}
      page="size: A4; margin: 14mm 12mm;"
      rootId="day-plan-print"
      dataCy="day-plan-print-frame"
    >
      <PrintDoc>
        <h1>{title}</h1>
        <div className="date">
          {date.toLocaleDateString(localeTag(currentLanguage), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
        <div className="totals">
          {`${t('calendar.dayPlan.pickups')}: ${plan.pickups.length} · ${t('calendar.dayPlan.returns')}: ${plan.returns.length} · ${t('calendar.dayPlan.ongoing')}: ${plan.ongoing.length}`}
        </div>

        {prepare.total > 0 && (
          <>
            <h2>{t('calendar.dayPlan.prepare')} ({t('calendar.dayPlan.pieces', { count: prepare.total })})</h2>
            {prepare.returningToday > 0 && (
              <div className="muted" data-cy="day-plan-print-prepare-returning">
                {t('calendar.dayPlan.prepareReturningToday', { count: prepare.returningToday })}
              </div>
            )}
            {itemsNamed ? (
              <table>
                <tbody>
                  {prepare.groups.map((group) => (
                    <tr key={group.type || ''}>
                      <td className="type">{group.type || t('calendar.dayPlan.noType')}</td>
                      <td className="count">×{group.count}</td>
                      <td>
                        {planGroupHasSizes(group)
                          ? group.sizes
                            .map((s) => `${s.size || t('calendar.dayPlan.noSize')} ×${s.count}`)
                            .join(', ')
                          : ''}
                      </td>
                    </tr>
                  ))}
                  {prepare.unknown > 0 && (
                    <tr>
                      <td className="type">{t('calendar.dayPlan.otherEquipment')}</td>
                      <td className="count">×{prepare.unknown}</td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <div className="muted">
                {equipmentState === 'failed' ? t('calendar.dayPlan.equipmentFailed') : t('calendar.dayPlan.equipmentLoading')}
              </div>
            )}
          </>
        )}

        {heading(t('calendar.dayPlan.pickups'), plan.pickups.length)}
        {plan.pickups.length > 0
          ? plan.pickups.map((e) => entry(e, 'pickup'))
          : <div className="muted">{t('calendar.dayPlan.pickupsEmpty')}</div>}

        {heading(t('calendar.dayPlan.returns'), plan.returns.length)}
        {plan.returns.length > 0
          ? plan.returns.map((e) => entry(e, 'return'))
          : <div className="muted">{t('calendar.dayPlan.returnsEmpty')}</div>}

        {heading(t('calendar.dayPlan.ongoing'), plan.ongoing.length)}
        {plan.ongoing.length > 0 ? (
          <table>
            <tbody>
              {plan.ongoing.map(({ reservation: r, items }) => (
                <tr key={r.id} data-cy="day-plan-print-ongoing">
                  <td className="name">{clientName(r)}</td>
                  <td>{`${day(r.start_date)} - ${day(r.end_date)}`}</td>
                  <td className="count">{t('calendar.dayPlan.pieces', { count: items.length })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="muted">{t('calendar.dayPlan.ongoingEmpty')}</div>
        )}

        {printedAt && (
          <div className="footer">
            {t('calendar.dayPlan.printedAt', { date: formatDateTime(printedAt, { locale: currentLanguage }) })}
          </div>
        )}
      </PrintDoc>
    </PrintFrame>
  );
}

export default React.memo(DayPlanPrint);
